import { useState, useEffect } from "react";
import { useModuleData } from "@/hooks/useModuleData";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useNavigate } from "react-router-dom";
import { Loader } from "@/components/common/Loader";
import { eur, num } from "@/lib/format";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Gauge, TrendingUp, Factory, Wallet, Banknote, Warehouse, ShieldCheck,
  AlertTriangle, Truck, PackageX, ArrowUpRight, Radio, Activity, Timer, Layers, AlertOctagon,
} from "lucide-react";

const RED = "#D0313A";
const CHARCOAL = "#161d23";
const PSTATUS = { RELEASED: "#3B82F6", IN_PROGRESS: "#F59E0B", CONFIRMED: "#10B981", CREATED: "#64748B", TECO: "#06B6D4" };
const SEV = {
  critical: { chip: "bg-red-100 text-red-700", dot: "#EF4444" },
  warning: { chip: "bg-amber-100 text-amber-700", dot: "#F59E0B" },
  info: { chip: "bg-sky-100 text-sky-700", dot: "#0a6ed1" },
};

const fmtMonth = (m) => {
  const [y, mo] = (m || "").split("-");
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return isNaN(d) ? m : d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

const tip = { background: "#fff", border: "1px solid #e5e9f0", borderRadius: 8, color: "#32363a", fontSize: 12 };

function Stat({ label, value, sub, accent = CHARCOAL, icon: Icon, onClick, testId }) {
  return (
    <div data-testid={testId} onClick={onClick} role={onClick ? "button" : undefined}
      className={`group rounded-xl border border-[#e5e9f0] bg-white p-4 shadow-sm transition-all ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md" : ""}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wide text-[#6a6d70]">{label}</span>
        {Icon && <Icon size={16} style={{ color: accent }} />}
      </div>
      <div className="mt-2 text-2xl font-bold tracking-tight" style={{ color: accent }}>{value}</div>
      {sub && <div className="mt-1 text-xs text-[#6a6d70]">{sub}</div>}
    </div>
  );
}

function Panel({ title, action, children, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-[#e5e9f0] bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[#32363a]">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function HMetric({ icon: I, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <I size={14} className="text-white/40" />
      <div>
        <div className="font-mono text-sm font-semibold text-white">{value}</div>
        <div className="text-[10px] uppercase tracking-wide text-white/50">{label}</div>
      </div>
    </div>
  );
}

function McsHealthStrip() {
  const [h, setH] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = async () => { try { const { data } = await api.get("/asrs/mcs-health"); if (alive) setH(data); } catch (e) { /* keep last */ } };
    load();
    const iv = setInterval(load, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  const online = h?.status === "ONLINE";
  return (
    <div data-testid="md-mcs-health" className="rounded-xl border border-[#e5e9f0] bg-[#0b1016] p-4 text-[#cbd5e1] shadow-sm">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <Radio size={16} className="text-[#0ea5e9]" />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-white/70">ASRS ↔ MCS Link</span>
          <span data-testid="mcs-health-status" className={`inline-flex items-center gap-1 rounded px-2 py-0.5 font-mono text-[10px] ${online ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${online ? "bg-emerald-400 animate-pulse" : "bg-red-400"}`} /> {h?.status || "…"}
          </span>
        </div>
        <HMetric icon={Activity} label="Telegrams/min" value={h ? num(h.telegrams_per_min) : "—"} />
        <HMetric icon={Timer} label="ACK latency" value={h ? `${h.ack_latency_ms} ms` : "—"} />
        <HMetric icon={Layers} label="Queue depth" value={h ? h.queue_depth : "—"} />
        <HMetric icon={Warehouse} label="Cranes online" value={h ? h.cranes_online : "—"} />
        <HMetric icon={Gauge} label="Uptime" value={h ? `${h.uptime_pct}%` : "—"} />
        <div className="ml-auto max-w-[300px] text-right">
          {h?.last_error
            ? <span className="inline-flex items-center gap-1 text-[11px] text-red-400"><AlertOctagon size={12} /> {h.last_error}</span>
            : <span className="text-[11px] text-emerald-400/80">No faults · all telegrams ACKed</span>}
        </div>
      </div>
    </div>
  );
}

export default function ExecutiveSummary() {
  const { data, loading } = useModuleData("/executive-summary");
  const { current } = useSite();
  const navigate = useNavigate();
  if (loading || !data) return <Loader label="Compiling company overview…" />;

  const k = data.kpis;
  const s = data.sales;
  const w = data.warehouse;
  const completion = k.target_units ? Math.round((k.produced_units / k.target_units) * 100) : 0;

  return (
    <div data-testid="md-summary-page" className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl border border-[#e5e9f0] bg-[#161d23] p-6 text-white shadow-sm">
        <div className="absolute inset-y-0 left-0 w-1.5 bg-[#D0313A]" />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-white/60">
              <Gauge size={14} className="text-[#f0575f]" /> {data.scope} · Executive scope
            </div>
            <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Managing Director Summary</h1>
            <p className="mt-1 text-sm text-white/70">Top-level company overview across sales, production, warehouse, finance & quality.</p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wide text-white/60">Total Sales Value</div>
            <div className="text-3xl font-bold text-[#f0575f]">{eur(k.total_sales)}</div>
            <div className="text-xs text-white/60">{num(k.order_count)} orders · {num(k.open_orders)} open</div>
          </div>
        </div>
      </div>

      {/* KPI hero grid */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat testId="md-kpi-sales" label="Sales Value" value={eur(k.total_sales)} sub={`${num(k.invoiced_orders)} invoiced`} accent="#8B5CF6" icon={TrendingUp} onClick={() => navigate("/sales")} />
        <Stat testId="md-kpi-produced" label="Units Produced" value={num(k.produced_units)} sub={`${completion}% of target · ${k.avg_scrap}% scrap`} accent="#3B82F6" icon={Factory} onClick={() => navigate("/production")} />
        <Stat testId="md-kpi-cash" label="Cash Position" value={eur(k.cash_position)} sub="Bank & cash (GL)" accent="#10B981" icon={Banknote} onClick={() => navigate("/finance")} />
        <Stat testId="md-kpi-spend" label="Procurement Spend" value={eur(k.procurement_spend)} sub={`${num(k.po_count)} purchase orders`} accent="#F97316" icon={Wallet} onClick={() => navigate("/purchasing")} />
        <Stat testId="md-kpi-ar" label="Receivables Open" value={eur(k.ar_open)} sub={`${eur(k.ar_overdue)} overdue`} accent="#EF4444" icon={ArrowUpRight} onClick={() => navigate("/finance")} />
        <Stat testId="md-kpi-wh" label="Warehouse Utilisation" value={`${k.warehouse_utilisation}%`} sub={`${num(w.occupied_bins)} bins occupied`} accent="#EC4899" icon={Warehouse} onClick={() => navigate("/warehouse")} />
        <Stat testId="md-kpi-qm" label="QM Pass Rate" value={`${k.qm_pass_rate}%`} sub="Inspection lots" accent="#EAB308" icon={ShieldCheck} onClick={() => navigate("/quality")} />
        <Stat testId="md-kpi-deliveries" label="Open Deliveries" value={num(k.open_deliveries)} sub={`${num(k.active_cranes)} ASRS cranes active`} accent="#0EA5E9" icon={Truck} onClick={() => navigate("/deliveries")} />
      </div>

      <McsHealthStrip />

      {/* Sales performance */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel testId="md-sales-trend" title="Sales Performance — Monthly" >
          <div className="h-64" style={{ minHeight: 256 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={240}>
              <AreaChart data={s.trend} margin={{ left: -12, right: 8, top: 6 }}>
                <defs>
                  <linearGradient id="mdRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={RED} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={RED} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
                <XAxis dataKey="month" tickFormatter={fmtMonth} stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `£${num(v / 1000)}k`} />
                <Tooltip contentStyle={tip} labelFormatter={fmtMonth} formatter={(v) => eur(v)} />
                <Area type="monotone" dataKey="revenue" stroke={RED} strokeWidth={2} fill="url(#mdRev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel testId="md-sales-by-site" title="Sales by Site">
          <div className="h-64" style={{ minHeight: 256 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={240}>
              <BarChart data={s.revenue_by_site} margin={{ left: -12, right: 8, top: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
                <XAxis dataKey="site" stroke="#64748B" fontSize={10} />
                <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `£${num(v / 1000)}k`} />
                <Tooltip contentStyle={tip} formatter={(v) => eur(v)} />
                <Bar dataKey="revenue" fill="#354a5f" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel testId="md-top-customers" title="Top Customers (YTD)">
          <div className="space-y-2">
            {s.top_customers.map((c, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-[#eef1f5] px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-[#32363a]">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-wide text-[#6a6d70]">{c.region}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm font-semibold text-[#32363a]">{eur(c.sales_ytd)}</div>
                  <div className="text-[10px] text-emerald-600">{eur(c.margin_ytd)} margin</div>
                </div>
              </div>
            ))}
            {s.top_customers.length === 0 && <div className="py-6 text-center text-sm text-[#6a6d70]">No customer sales in scope</div>}
          </div>
        </Panel>
      </div>

      {/* Production + Warehouse + Issues */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel testId="md-production" title="Production Status">
          <div className="h-56" style={{ minHeight: 224 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={210}>
              <PieChart>
                <Pie data={data.production.status} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={48} outerRadius={80} paddingAngle={3}
                  label={({ count }) => count} labelLine={false} isAnimationActive={false}>
                  {data.production.status.map((e) => <Cell key={e.status} fill={PSTATUS[e.status] || "#64748B"} />)}
                </Pie>
                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central">
                  <tspan x="50%" dy="-0.3em" className="fill-[#32363a] text-xl font-bold">{data.production.status.reduce((a, e) => a + (e.count || 0), 0)}</tspan>
                  <tspan x="50%" dy="1.5em" className="fill-[#6a6d70] text-[10px] uppercase tracking-wide">Orders</tspan>
                </text>
                <Tooltip contentStyle={tip} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {data.production.status.map((e) => (
              <span key={e.status} className="flex items-center gap-1.5 text-[11px] text-[#6a6d70]">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: PSTATUS[e.status] || "#64748B" }} />
                {e.status.replace(/_/g, " ")} ({e.count})
              </span>
            ))}
          </div>
        </Panel>

        <Panel testId="md-warehouse" title="Warehouse Performance">
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-xs text-[#6a6d70]"><span>Bin utilisation</span><span className="font-mono">{w.utilisation}%</span></div>
              <div className="mt-1 h-2.5 rounded-full bg-[#eef1f5]"><div className="h-2.5 rounded-full" style={{ width: `${w.utilisation}%`, background: RED }} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MiniStat label="Bins occupied" value={`${num(w.occupied_bins)}/${num(w.total_bins)}`} />
              <MiniStat label="Open tasks" value={num(w.tasks_open)} />
              <MiniStat label="Tasks confirmed" value={num(w.tasks_confirmed)} />
              <MiniStat label="Open deliveries" value={num(w.open_deliveries)} />
              <MiniStat label="ASRS cranes active" value={num(w.active_cranes)} />
              <MiniStat label="Inventory variances" value={num(w.variances)} danger={w.variances > 0} />
            </div>
          </div>
        </Panel>

        <Panel testId="md-finance" title="Finance Snapshot">
          <div className="grid grid-cols-2 gap-3">
            <MiniStat label="Cash & bank" value={eur(data.finance.cash)} />
            <MiniStat label="AR open" value={eur(data.finance.ar_open)} />
            <MiniStat label="AR overdue" value={eur(data.finance.ar_overdue)} danger={data.finance.ar_overdue > 0} />
            <MiniStat label="AP open" value={eur(data.finance.ap_open)} />
            <MiniStat label="Low stock items" value={num(k.low_stock)} />
            <MiniStat label="Out of stock" value={num(k.out_stock)} danger={k.out_stock > 0} />
          </div>
        </Panel>
      </div>

      {/* Issues summary */}
      <Panel testId="md-issues" title={`Summary of Issues (${data.issues.length})`}
        action={<span className="flex items-center gap-1.5 text-[11px] font-medium text-[#6a6d70]"><AlertTriangle size={14} className="text-[#F59E0B]" /> Requires attention</span>}>
        {data.issues.length === 0 ? (
          <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-6 text-sm text-emerald-700"><ShieldCheck size={18} /> All clear — no open issues in this scope.</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.issues.map((iss, i) => {
              const sv = SEV[iss.severity] || SEV.info;
              return (
                <button key={i} data-testid={`md-issue-${i}`} onClick={() => navigate(iss.path)}
                  className="group flex flex-col rounded-lg border border-[#e5e9f0] bg-[#fbfcfe] p-3 text-left transition-all hover:shadow-md">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm font-semibold text-[#32363a]">
                      <span className="h-2 w-2 rounded-full" style={{ background: sv.dot }} />{iss.title}
                    </span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${sv.chip}`}>{iss.module}</span>
                  </div>
                  <span className="mt-1 text-xs text-[#6a6d70]">{iss.detail}</span>
                  <span className="mt-2 text-[11px] font-medium text-[#D0313A] group-hover:underline">Open module →</span>
                </button>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}

function MiniStat({ label, value, danger }) {
  return (
    <div className="rounded-lg border border-[#eef1f5] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-[#6a6d70]">{label}</div>
      <div className={`mt-0.5 font-mono text-sm font-semibold ${danger ? "text-[#bb0000]" : "text-[#32363a]"}`}>{value}</div>
    </div>
  );
}
